import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Keyboard, Mousewheel, Navigation } from 'swiper/modules';
import { useTranslation } from 'react-i18next';

import { joinClassNames, STORY_ACCENT_COLOR, STORY_SLIDE_WINDOW } from '../../util/util';
import { MessageHelper } from '../../util/message-helper';
import { useConfiguration } from '../../contexts/ConfigurationContext';
import { usePlayer } from '../../contexts/PlayerContext';

import {
	buildStoryFeedItems,
	firstStoryAdNumber,
	getFeedIndexForMediaIndex,
	getStoryAdStartVolume,
	getStoryThumbnailUrl,
	isStoryAdItem,
	isStoryVideoItem,
	shouldUseStoryAdSlots,
	STORY_AD_SKIP_AFTER_DEFAULT,
	StoryFeedItem,
} from './story-feed-helper';
import StoryVideoSlide from './StoryVideoSlide';
import StoryAdSlide from './StoryAdSlide';
import StoryAdPlaceholder from './StoryAdPlaceholder';

import './StoryFeed.scss';

// Clearance the step buttons and the close need beside the card before they are drawn at all:
// 60.5px to the disc's edge plus the 55px disc, per the design's placement.
const STORY_CHROME_GUTTER = 116;
// The chrome's icons are drawn here rather than taken from @material-ui/icons, whose Expand* and
// Close are filled glyphs: heavier, steeper and shorter than the design's. These are traced off the
// Figma frame - a thin stroke, a sharp mitred apex and square-cut ends - and sized so the ink lands
// on the measured 24 x 13.7 chevron and 25 x 25 cross.
const StoryChevron = ({ down }: { down?: boolean }) => (
	<svg
		className={joinClassNames(
			'qplayer-story-softbox__icon qplayer-story-softbox__icon--chevron',
			down ? 'qplayer-story-softbox__icon--chevron-down' : ''
		)}
		// Offset by half a unit so the ink, not the box, is what sits centred. The stroke spans
		// y 2-16 of a 17-tall box: 2 above, 1 below, so centring the box hangs the mark low.
		viewBox="0 0.5 28 17"
		fill="none"
		aria-hidden="true"
	>
		{/* Heavier than the 2 it was drawn at: at 26px wide this renders under 2px, which on a 55px
		    disc reads as a hairline rather than a control. */}
		<path d="M2 15L14 3l12 12" stroke="currentColor" strokeWidth="2.6" />
	</svg>
);

const StoryCross = () => (
	<svg className="qplayer-story-softbox__icon qplayer-story-softbox__icon--cross" viewBox="0 0 28 28" fill="none" aria-hidden="true">
		<path d="M4 4l20 20M24 4L4 24" stroke="currentColor" strokeWidth="2" />
	</svg>
);

// How long a slide takes to move. Shared by the Swiper below and by the retirement that waits for
// it: a slot taken away mid-transition is the flash this was written to remove.
const STORY_SLIDE_SPEED_MS = 380;
// Breathing space the logo keeps between itself and the card before it is dropped.
const STORY_LOGO_CLEARANCE = 16;
// How far left a swipe has to travel to close the feed, and how much more sideways than vertical it
// has to be. Well past Swiper's own threshold, so scrolling the stack never reads as an exit.
const STORY_CLOSE_SWIPE_PX = 80;
const STORY_CLOSE_SWIPE_RATIO = 2;

/**
 * Where the logo's right edge lands, from the page's left. Derived from the image's own proportions
 * inside the caps the stylesheet sets, rather than read off its box: the box is the thing we are
 * about to hide, and measuring it would collapse to zero and oscillate. Returns 0 while the image
 * has no intrinsic size, which reads as "fits" and leaves it alone.
 */
const logoWidth = (logo: HTMLImageElement | null): number => {
	if (!logo?.naturalWidth || !logo?.naturalHeight) {
		return 0;
	}
	const style = getComputedStyle(logo);
	const capWidth = parseFloat(style.maxWidth) || Infinity;
	const capHeight = parseFloat(style.maxHeight) || Infinity;
	const scale = Math.min(1, capHeight / logo.naturalHeight, capWidth / logo.naturalWidth);
	return (parseFloat(style.left) || 0) + logo.naturalWidth * scale;
};

interface StoryFeedProps {
	/** The resolved preset the ordinary player would have used. */
	settings: any;
	container?: any;
	isUsedOnMobile: boolean;
	getRestrictToken: (mediaId: string) => any;
	handlePlayerHover?: any;
}

/**
 * The reels feed: a vertical stack of single-media players, one visible, its neighbours warm.
 * Reads the embed from context like Player does, so App renders it in one gated line; geometry lives
 * on this component's own wrapper, so nothing depends on which element is above it.
 */
const StoryFeed = ({ settings, container, isUsedOnMobile, getRestrictToken, handlePlayerHover }: StoryFeedProps) => {
	const { t, i18n } = useTranslation();
	const { configParams, mediasData, currentMediaId, setCurrentMediaId, playlistId } = useConfiguration();
	const { isPlaying, currentVolumeLevel, setCurrentVolumeLevel, setPlayerCurrentPosition } = usePlayer();

	// Only the slides around the active one keep a mounted Player. Everything else stays a poster,
	// so opening a long story playlist does not initialise every media up front.
	const [storyActiveIndex, setStoryActiveIndex] = useState(0);
	// Swiper reports every slide inactive until it has initialised, and a Player creates its GoBrain
	// widget on mount - so every clip used to be created with `autoplay` off (player-helper disables
	// it for a slide that is not visible) and the opening one had to be poked into playing by a flat
	// one-second timer. That timer is the black screen at the start of a reel. Until Swiper reports
	// for itself, the opening slide is the active one.
	const [hasStorySwiperInitialised, setHasStorySwiperInitialised] = useState(false);
	const [storySwiper, setStorySwiper] = useState<any>(null);
	// Swiper drives these two buttons through its Navigation module even though they are rendered
	// outside the slider, beside the video rather than on top of it.
	// At the ends of a feed that no longer wraps, so the step buttons can say there is nowhere to go.
	const [storyAtStart, setStoryAtStart] = useState(true);
	const [storyAtEnd, setStoryAtEnd] = useState(false);
	const [storyPrevEl, setStoryPrevEl] = useState<HTMLElement | null>(null);
	const [storyNextEl, setStoryNextEl] = useState<HTMLElement | null>(null);
	// An ad slot has no content, so the player's controls have nothing to control: while one is on
	// screen they are unmounted rather than hidden, so nothing is left focusable over the ad.
	const [isStoryAdSlotActive, setIsStoryAdSlotActive] = useState(false);
	// Held separately: a slot hides the controls for as long as it is on screen, but it only holds
	// the viewer in place while an ad is actually running. An empty slot hands navigation back.
	const [isStoryAdLocked, setIsStoryAdLocked] = useState(false);
	// Slots that have already run - or failed to fill - are remembered, so scrolling back over one
	// passes straight through instead of landing on an empty slide.
	const [consumedAdSlots, setConsumedAdSlots] = useState<string[]>([]);
	// Slots the ad server had nothing for. Because the request now goes out while the slide is only
	// a neighbour, this is usually known BEFORE the viewer gets there - which is what lets the feed
	// step over the slot instead of parking them on a blank one.
	const [emptyAdSlots, setEmptyAdSlots] = useState<string[]>([]);
	const emptyAdSlotsRef = useRef<string[]>([]);
	emptyAdSlotsRef.current = emptyAdSlots;
	// Same reason, for slots whose break has already been watched.
	const consumedAdSlotsRef = useRef<string[]>([]);
	consumedAdSlotsRef.current = consumedAdSlots;
	// Which clip is actually painting frames, so its still frame comes down at the right moment.
	const [storyPictureMediaId, setStoryPictureMediaId] = useState<string | null>(null);

	// Direction of travel, so leaving an ad slot continues the way the viewer was already going.
	const storyAdExitForwards = useRef(true);
	// The clip the feed was on before this one. Swiper's own previousIndex is a position among the
	// slides it shuffles in loop mode, which is not the same thing.
	const storyLastIndex = useRef(0);
	// Ad slots the feed has stood on, by key. Retired on the way out of one; see handleStoryIndexChange.
	const storyVisitedAdSlots = useRef<Set<string>>(new Set());
	const lastEmittedStoryIndex = useRef<string | null>(null);

	// Where a deep link opens. initMediaId wins: it survives an editor reordering the playlist.
	const storyMediaInitPosition = useMemo(() => {
		if (configParams?.initMediaId) {
			const requested = (mediasData ?? []).findIndex(
				({ id, originalId }: any) => id === configParams.initMediaId || originalId === configParams.initMediaId
			);
			if (requested >= 0) {
				return requested;
			}
		}
		const position = configParams?.initPosition ? +configParams.initPosition : 0;
		if (!Number.isFinite(position) || position < 0) {
			return 0;
		}
		return Math.min(position, Math.max(0, (mediasData?.length ?? 1) - 1));
	}, [configParams?.initMediaId, configParams?.initPosition, mediasData]);

	// A clip from another playlist opens at the first one instead - usually a card on the wrong
	// feed. After mediasData loads, or every feed reports it while the playlist is empty.
	useEffect(() => {
		const requested = configParams?.initMediaId;
		if (!requested || !mediasData?.length) {
			return;
		}
		const found = mediasData.some(({ id, originalId }: any) => id === requested || originalId === requested);
		if (!found) {
			console.warn(`[QPlayer] ${requested} is not in this playlist; the feed opened at the first clip`);
		}
	}, [configParams?.initMediaId, mediasData]);

	// The preset's, overridden per placement by an attribute. A typo falls through rather than
	// counting as 0, which would turn the ads off or make one unskippable.
	const adSettings = settings?.advertisementSettings;
	const storyAdInterval = firstStoryAdNumber([configParams?.storyAdInterval, adSettings?.storyAdInterval], 0);
	const storyAdTagUrl =
		configParams?.storyAdTag || (adSettings?.enabled ? adSettings?.advertisementServer : undefined);
	// Only where the creative carries no skipoffset. This is the navigation unlock, not a second
	// opinion on ad ops' policy: without it an unskippable ad traps the viewer. 0 removes it.
	const storyAdSkipAfter = firstStoryAdNumber(
		[configParams?.storyAdSkipAfter, adSettings?.storyAdSkipAfter],
		STORY_AD_SKIP_AFTER_DEFAULT
	);
	const storyAdSlotsEnabled = shouldUseStoryAdSlots({
		adTagUrl: storyAdTagUrl,
		adInterval: storyAdInterval,
		configParams,
	});

	const storyFeedItems: StoryFeedItem[] = useMemo(
		() =>
			buildStoryFeedItems({
				medias: mediasData ?? [],
				adInterval: storyAdInterval,
				adsEnabled: storyAdSlotsEnabled,
			}),
		[mediasData, storyAdInterval, storyAdSlotsEnabled]
	);
	// A slot with nothing left in it is not hidden or stepped over - it is not there. An empty one
	// (the ad server had nothing) and a spent one (the break has been watched) are the same thing
	// from two directions: a card with no content. Taking it out of the list is what lets the feed
	// scroll clip to clip with its own animation, which skipping past a slide cannot do.
	const storyVisibleItems: StoryFeedItem[] = useMemo(
		() =>
			storyFeedItems.filter(
				(item) =>
					!isStoryAdItem(item) || !(emptyAdSlots.includes(item.key) || consumedAdSlots.includes(item.key))
			),
		[storyFeedItems, emptyAdSlots, consumedAdSlots]
	);
	// Read inside handlers bound to Swiper. They can fire from inside Swiper's own loop rebuild,
	// when the handler attached still closes over the list as it was BEFORE the change.
	const storyItemsRef = useRef<StoryFeedItem[]>([]);
	storyItemsRef.current = storyVisibleItems;

	const storyInitialSlide = getFeedIndexForMediaIndex(storyVisibleItems, storyMediaInitPosition);

	// GoBrain's ad module is switched off only where our slots actually replace it - otherwise a
	// story customer with preset ads and no interval loses their pre-rolls and gets nothing back.
	const storyNoAdConfig =
		storyAdSlotsEnabled && settings?.advertisementSettings?.enabled
			? { ...settings, advertisementSettings: { ...settings.advertisementSettings, enabled: false } }
			: settings;

	// The clip in focus. Every slide is its own player, keyed by media, so this is the only widget
	// the chrome, the audio and the keyboard shortcuts should ever talk to.
	const storyActivePlayerId = `player-${currentMediaId}-${configParams?.containerKey}`;

	// The backdrop is the clip in focus, blurred. An ad slot has no frame, so walk back to the clip
	// that led into it.
	const storyBackdropThumb = useMemo(() => {
		for (let index = Math.min(storyActiveIndex, storyVisibleItems.length - 1); index >= 0; index -= 1) {
			const item = storyVisibleItems[index];
			if (isStoryVideoItem(item)) {
				return getStoryThumbnailUrl(item.media);
			}
		}
		return '';
	}, [storyActiveIndex, storyVisibleItems]);

	// Audio is one setting for the whole feed rather than one per slide: an ad slot that comes up
	// while the video is muted starts muted, and unmuting an ad unmutes the clip it hands over to.
	const storyAudibleVolume = useRef(0.5);
	useEffect(() => {
		if (typeof currentVolumeLevel === 'number' && currentVolumeLevel > 0) {
			storyAudibleVolume.current = currentVolumeLevel;
		}
	}, [currentVolumeLevel]);
	const isStoryMuted = !currentVolumeLevel;
	const handleStoryMutedChange = useCallback(
		(muted: boolean) => {
			const volume = muted ? 0 : storyAudibleVolume.current || 0.5;
			setCurrentVolumeLevel(volume);
			// The clip underneath the ad is already mounted, so it is told directly. Waiting for it
			// to pick the level up as it becomes visible would leave the handover a slide behind.
			(window as any).GoBrain?.widgets?.(storyActivePlayerId)?.volume?.(volume);
		},
		[storyActivePlayerId, setCurrentVolumeLevel]
	);

	// Which clip is on screen. The only source the page has: one player per slide means GoBrain's
	// queue - and with it queueItem - never runs.
	const emitStoryClipChanged = useCallback(
		(item: StoryFeedItem, feedIndex: number, totalVideos: number) => {
			// Identified by what it IS, not where it sits. A feed index stopped being a stable name
			// for a clip once a slot could be taken out of the list: retiring one renumbers m3 from
			// index 4 to index 3, and an index-keyed guard reads that as two different clips and
			// announces it twice - which a host counting views would count twice.
			const identity = isStoryVideoItem(item) ? `media:${item.media?.id}` : `slot:${feedIndex}`;
			if (!item || lastEmittedStoryIndex.current === identity) {
				// Swiper also reports an index change while initialising at a non-zero slide, which
				// would otherwise announce the opening clip twice.
				return;
			}
			lastEmittedStoryIndex.current = identity;
			const isVideo = isStoryVideoItem(item);
			MessageHelper.sendMessage({
				type: MessageHelper.MESSAGE_TYPES.storyClipChanged,
				mediaId: isVideo ? item.media?.id : '',
				metadata: {
					// The host must not put an ad slot in the address bar: it would publish shareable
					// links to an ad and count ad views as editorial page views.
					slotType: item.type,
					playlistId,
					feedIndex,
					mediaIndex: isVideo ? item.mediaIndex : null,
					totalVideos,
					title: isVideo ? item.media?.mediaName : undefined,
				},
				qplayerId: configParams?.qplayerId as string,
			});
		},
		[playlistId, configParams?.qplayerId]
	);

	// Everything the feed reports to the host goes through here, so the envelope - and the qplayerId
	// that tells two players on one page apart - is built once.
	const emitFeedEvent = useCallback(
		(type: string, metadata: any = {}) => {
			MessageHelper.sendMessage({
				type,
				mediaId: currentMediaId || '',
				metadata: { playlistId, ...metadata },
				qplayerId: configParams?.qplayerId as string,
			});
		},
		[currentMediaId, playlistId, configParams?.qplayerId]
	);

	// Opened and closed, as a pair. The close is the cleanup so it fires however the feed goes away -
	// Escape, the close button, Back, or the element unmounting under it.
	const feedLifecycle = useRef({ emitFeedEvent, storyFeedItems: storyVisibleItems, mediasData });
	feedLifecycle.current = { emitFeedEvent, storyFeedItems: storyVisibleItems, mediasData };
	useEffect(() => {
		const { emitFeedEvent: emit, mediasData: medias } = feedLifecycle.current;
		emit(MessageHelper.MESSAGE_TYPES.storyFeedOpened, { totalVideos: medias?.length ?? 0 });
		return () => {
			const current = feedLifecycle.current;
			current.emitFeedEvent(MessageHelper.MESSAGE_TYPES.storyFeedClosed, {
				totalVideos: current.mediasData?.length ?? 0,
			});
		};
		// Once per mount: this is the feed opening and closing, not a clip changing.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const toggleStoryPlayback = useCallback(() => {
		const widget = (window as any).GoBrain?.widgets?.(storyActivePlayerId);
		if (!widget) {
			return;
		}
		// Deliberately the reducer and not the live <video>. On a desktop GoBrain toggles playback
		// on the click itself, so by the time this runs the element already reports the new state -
		// reading it here made the two cancel out and the click did nothing at all.
		isPlaying ? widget.pause?.() : widget.play?.();
	}, [isPlaying, storyActivePlayerId]);

	// Reaching the end is what moves the feed on - 1c turned repeat off for exactly this. The feed
	// stops at the last clip rather than wrapping, so slideNext there is a no-op and the clip simply
	// ends, which is what the step button says by being disabled.
	// The preset's "replay" decides what the end of a clip means in a feed: loop it, or move on.
	// Other sites loop, so this has to be expressible rather than assumed. The player is told to
	// repeat as well (see createPlayerInstance); the guard here is what keeps the two from
	// disagreeing if the framework reports a completion on a repeating clip anyway - in which case
	// the feed would otherwise scroll out from under a video that is still playing.
	const shouldLoopStoryClip = !!settings?.playbackOptions?.replay;

	const handleStoryVideoEnded = useCallback(() => {
		if (shouldLoopStoryClip) {
			return;
		}
		storySwiper?.slideNext?.();
	}, [storySwiper, shouldLoopStoryClip]);

	// An ad slot has nothing to play, so when its break ends the feed moves on by itself.
	const handleStoryAdFinished = useCallback(
		(adKey: string) => {
			const items = storyItemsRef.current;
			const slotIndex = items.findIndex((item) => item.key === adKey);
			if (slotIndex !== -1) {
				const forwards = storyAdExitForwards.current;
				const order = forwards
					? [...items.slice(slotIndex + 1), ...items.slice(0, slotIndex)]
					: [...items.slice(0, slotIndex).reverse(), ...items.slice(slotIndex + 1).reverse()];
				const resume = order.find((item) => isStoryVideoItem(item));
				storyResumeMediaId.current = isStoryVideoItem(resume) ? resume.media?.id ?? null : null;
			}
			if (!storySwiper) {
				setConsumedAdSlots((previous) => (previous.includes(adKey) ? previous : [...previous, adKey]));
				return;
			}

			// SCROLL first, with the feed's own animation, exactly as a swipe would. The break ending
			// should look like leaving a clip, not like the screen being replaced - and the slot is
			// still in the list at this moment, so the slide it moves to is the right one.
			if (!storyAdExitForwards.current && !storySwiper.isBeginning) {
				storySwiper.slidePrev?.();
			} else {
				storySwiper.slideNext?.();
			}

			// ...and take the slot out only once that has finished. Removing it mid-transition is
			// what made this flash: the list changes under a slide that is still moving, so Swiper
			// re-anchors to a different clip part-way through. Behind the viewer, after they have
			// arrived, the removal is invisible - the slide they are watching simply renumbers.
			window.setTimeout(() => {
				setConsumedAdSlots((previous) => (previous.includes(adKey) ? previous : [...previous, adKey]));
			}, STORY_SLIDE_SPEED_MS + 60);
		},
		[storySwiper]
	);

	// Taking a slide out shifts every index after it, and Swiper rebuilds its loop around the new
	// list - so an index recorded before the change means nothing after it. The clip the viewer is
	// watching does survive, though, because it has an id. So the rule is simply: after the list
	// changes, whoever was on screen should still be on screen.
	//
	// Only on a LENGTH change. Correcting on every render would fight ordinary navigation, where
	// Swiper is mid-transition and briefly reports the slide being left behind.
	const storyPrevCount = useRef(0);
	// The clip to land on after a break, named before the slot goes.
	const storyResumeMediaId = useRef<string | null>(null);
	useLayoutEffect(() => {
		const count = storyVisibleItems.length;
		const previousCount = storyPrevCount.current;
		storyPrevCount.current = count;
		if (previousCount === 0 || previousCount === count || !storySwiper || storySwiper.destroyed || !count) {
			return;
		}
		// The clip chosen when the break ended, if there was one; otherwise whoever was on screen.
		const anchorId = storyResumeMediaId.current ?? currentMediaId;
		storyResumeMediaId.current = null;
		const wanted = storyVisibleItems.findIndex(
			(item) => isStoryVideoItem(item) && item.media?.id === anchorId
		);
		if (wanted === -1) {
			return;
		}
		if ((storySwiper.realIndex ?? 0) !== wanted) {
			// No animation: nothing moved as far as the viewer is concerned. The slide they are
			// watching simply has a different number now.
			// slideTo, not slideToLoop: the latter translates an index out of loop space, and
			// there is no loop space any more.
			storySwiper.slideTo?.(wanted, 0);
		}
		storyLastIndex.current = wanted;
		setStoryActiveIndex(wanted);
		// Whatever was holding them for an ad went with the slot.
		setIsStoryAdSlotActive(false);
		setIsStoryAdLocked(false);
	}, [storyVisibleItems, storySwiper, currentMediaId]);

	const handleStoryIndexChange = useCallback(
		(swiper: any) => {
			// realIndex still reads the clip. Without loop it equals activeIndex, but reading it
			// keeps this correct either way.
			const nextIndex = swiper?.realIndex ?? 0;
			const items = storyItemsRef.current;
			const nextItem = items[nextIndex];
			if (!nextItem) {
				return;
			}
			(window as any).GoBrain?.widgets?.(`player-${currentMediaId}-${configParams?.containerKey}`)?.pause?.();
			setStoryActiveIndex(nextIndex);
			setStoryAtStart(!!swiper?.isBeginning);
			setStoryAtEnd(!!swiper?.isEnd);

			// Read from the clip left behind, not Swiper's previousIndex, which is in loop space. A
			// turn of the loop counts as forwards.
			const previousIndex = storyLastIndex.current;
			storyLastIndex.current = nextIndex;
			const isAdSlotItem = isStoryAdItem(nextItem);

			const wrappedForwards = previousIndex === items.length - 1 && nextIndex === 0;
			const wrappedBackwards = previousIndex === 0 && nextIndex === items.length - 1;
			storyAdExitForwards.current = wrappedForwards || (!wrappedBackwards && nextIndex >= previousIndex);

			// A slot the feed has stood on is spent once it moves on, whatever became of it. The slot
			// cannot report this itself: it only knows it was passed if React rendered it active at
			// least once, and two steps inside one transition move Swiper's activeIndex twice before
			// any render - so it was stepped over, never retired, and still there on the way back.
			// This event fires on every index change whatever React is doing.
			//
			// By key, not by index: a removal renumbers everything after it. Deferred by the
			// transition, because changing the list mid-move is what wedged Swiper, and idempotent
			// with handleStoryAdFinished, which schedules the same removal when it drives the exit.
			if (isAdSlotItem) {
				storyVisitedAdSlots.current.add(nextItem.key);
			} else if (storyVisitedAdSlots.current.size) {
				const spent = Array.from(storyVisitedAdSlots.current);
				storyVisitedAdSlots.current.clear();
				window.setTimeout(() => {
					setConsumedAdSlots((previous) => {
						const missing = spent.filter((key) => !previous.includes(key));
						return missing.length ? [...previous, ...missing] : previous;
					});
				}, STORY_SLIDE_SPEED_MS + 60);
			}

			emitStoryClipChanged(nextItem, nextIndex, mediasData?.length ?? 0);
			// The incoming clip starts at nought; leaving the outgoing clip's position behind would
			// show its progress under the new one until the first tick.
			setPlayerCurrentPosition(0);

			setIsStoryAdSlotActive(isAdSlotItem);
			// Only an ad about to run holds the viewer. A spent or empty slot leaves them free.
			setIsStoryAdLocked(isAdSlotItem && !consumedAdSlots.includes(nextItem.key));
			if (!isStoryVideoItem(nextItem)) {
				return;
			}

			const nextMediaId = nextItem.media?.id;
			(window as any).GoBrain?.widgets?.(`player-${nextMediaId}-${configParams?.containerKey}`)?.play?.();
			setCurrentMediaId(nextMediaId);


		},
		[
			storyVisibleItems,
			currentMediaId,
			configParams?.containerKey,
			setCurrentMediaId,
			consumedAdSlots,
			emitStoryClipChanged,
			mediasData?.length,
			setPlayerCurrentPosition,
			storyInitialSlide,
		]
	);

	// Drag, wheel and arrow keys are shut off during an ad too, or the skip button is decoration.
	// slideNext() still works, which is how the ad slide moves the feed on.
	useEffect(() => {
		if (!storySwiper) {
			return;
		}
		storySwiper.allowTouchMove = !isStoryAdLocked;
		if (isStoryAdLocked) {
			storySwiper.mousewheel?.disable?.();
			storySwiper.keyboard?.disable?.();
		} else {
			storySwiper.mousewheel?.enable?.();
			storySwiper.keyboard?.enable?.();
		}
	}, [storySwiper, isStoryAdLocked]);

	// Space is the one shortcut the feed adds. Ignored over the chrome and over an ad.
	useEffect(() => {
		const handleSpacebar = (event: KeyboardEvent) => {
			if (event.code !== 'Space' && event.key !== ' ') {
				return;
			}
			const target = event.target as HTMLElement | null;
			if (target?.closest?.('button, a, input, textarea, select, [role="button"]')) {
				return;
			}
			if (isStoryAdSlotActive) {
				return;
			}
			event.preventDefault();
			toggleStoryPlayback();
		};
		document.addEventListener('keydown', handleSpacebar);
		return () => document.removeEventListener('keydown', handleSpacebar);
	}, [toggleStoryPlayback, isStoryAdSlotActive]);

	// The step buttons sit beside the card, never on it, so they disappear where there is no gutter.
	// Measured, not guessed at a breakpoint: card width comes from viewport HEIGHT, so a wide window
	// can still have no room.
	// Shown once when a feed opens, because a single still frame does not say there is a stack behind
	// it - least of all to someone who arrived on a link rather than through a gallery.
	const [storyFrameEl, setStoryFrameEl] = useState<HTMLDivElement | null>(null);
	const [storyLogoEl, setStoryLogoEl] = useState<HTMLImageElement | null>(null);
	const [storyChromeFits, setStoryChromeFits] = useState(false);
	const [storyLogoFits, setStoryLogoFits] = useState(true);
	useEffect(() => {
		if (!storyFrameEl) {
			return undefined;
		}
		const measure = () => {
			const stage = storyFrameEl.querySelector('.qplayer-story-softbox__stage');
			if (!stage) {
				// Nothing to measure against yet; the slides bring their own resize with them.
				return;
			}
			const gutter = (storyFrameEl.clientWidth - stage.clientWidth) / 2;
			setStoryChromeFits(gutter >= STORY_CHROME_GUTTER);
			setStoryLogoFits(logoWidth(storyLogoEl) + STORY_LOGO_CLEARANCE <= gutter);
		};
		measure();
		// naturalWidth is 0 until the image arrives, and a customer's logo is a network away.
		storyLogoEl?.addEventListener('load', measure);
		const stopObserving = (() => {
			if (typeof ResizeObserver === 'undefined') {
				window.addEventListener('resize', measure);
				return () => window.removeEventListener('resize', measure);
			}
			const observer = new ResizeObserver(measure);
			observer.observe(storyFrameEl);
			return () => observer.disconnect();
		})();
		return () => {
			storyLogoEl?.removeEventListener('load', measure);
			stopObserving();
		};
	}, [storyFrameEl, storyLogoEl, mediasData?.length, storyActiveIndex]);

	// sendMessage covers both hosts: posted to the parent when the player runs in the loader's
	// iframe, and to the page itself when it is embedded as a web component.
	const handleCloseStoryFeed = useCallback(() => {
		(window as any).GoBrain?.widgets?.(storyActivePlayerId)?.pause?.();
		MessageHelper.sendMessage({
			type: MessageHelper.MESSAGE_TYPES.closePlayer,
			mediaId: playlistId || currentMediaId,
			metadata: {},
			qplayerId: configParams?.qplayerId as string,
		});
	}, [storyActivePlayerId, playlistId, currentMediaId, configParams?.qplayerId]);

	// A feed opened as a softbox owns the whole viewport; one that fills a box on the page does not.
	const isOverlay = configParams?.inline === 'off';

	// Keyboard access, WCAG 2.4.3. An overlay feed is a modal over the page, but nothing used to move
	// the focus into it or keep it there: Tab walked the gallery BEHIND the overlay, and pressing
	// Enter on a card there changed the clip. That is the audit's "Tab advances to the next video in
	// the playlist", and why mute could only be reached on the last one.
	//
	// Arrow up/down already change clips (Swiper's Keyboard module, bound to the document) and Space
	// and Enter already operate a focused control, so the missing half is purely focus management:
	// move it in, hold it in, give it back.
	useEffect(() => {
		if (!storyFrameEl || !isOverlay) {
			return undefined;
		}
		// Whoever opened the feed - a gallery card, a button on the page - gets the focus back when
		// it closes, which is the other half of 2.4.3.
		const opener = document.activeElement as HTMLElement | null;
		// The controls are a SIBLING of the feed, not a child, so the trap is scoped to the player
		// root rather than to the softbox; scoping it to the softbox would lock the controls out.
		const root = (storyFrameEl.closest('.qplayer-app') ?? storyFrameEl.parentElement ?? storyFrameEl) as HTMLElement;
		const tabbable = () => {
			const nodes = Array.from(
				root.querySelectorAll<HTMLElement>('a[href], button, input, select, textarea, [tabindex]')
			).filter(
				(el) =>
					el.tabIndex >= 0 &&
					!el.hasAttribute('disabled') &&
					// Only the clip on screen. The slides either side are mounted too, and their text
					// and controls would otherwise sit between the viewer and the controls of the clip
					// they are actually watching - tab from one "Show more" and you land on the next
					// clip's, never on this clip's mute. StoryClipInfo also marks them -1; this is the
					// backstop for anything else a mounted slide puts in the tab order.
					!el.closest('.swiper-slide:not(.swiper-slide-active)') &&
					el.getAttribute('aria-hidden') !== 'true' &&
					// getClientRects, not offsetParent: the feed and its chrome are position: fixed,
					// for which offsetParent is null even when the element is plainly on screen.
					el.getClientRects().length > 0
			);
			// The clip's own controls first. In the DOM the feed's chrome comes before them, so a
			// viewer reaching for mute or share had to pass the step buttons - the audit called that
			// out, and a step button is one Enter away from changing the clip they were watching.
			return nodes.sort(
				(a, b) => Number(!!b.closest('.controlsOverlay')) - Number(!!a.closest('.controlsOverlay'))
			);
		};

		storyFrameEl.focus({ preventScroll: true });

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Tab') {
				return;
			}
			const list = tabbable();
			if (!list.length) {
				return;
			}
			const current = document.activeElement as HTMLElement | null;
			const index = current ? list.indexOf(current) : -1;
			// Always handled here: left to the browser, Tab from the last control walks out of the
			// dialog and into the page underneath, which is the bug being fixed.
			event.preventDefault();
			const next = event.shiftKey
				? list[index <= 0 ? list.length - 1 : index - 1]
				: list[index === -1 || index === list.length - 1 ? 0 : index + 1];
			next?.focus({ preventScroll: true });
		};

		document.addEventListener('keydown', onKeyDown, true);
		return () => {
			document.removeEventListener('keydown', onKeyDown, true);
			opener?.focus?.({ preventScroll: true });
		};
	}, [storyFrameEl, isOverlay]);

	// A swipe left closes the feed, and is the way out on a phone, where there is no gutter for the
	// close button to sit in. Touch events only, so a mouse drag on a desktop never triggers it.
	useEffect(() => {
		if (!storyFrameEl || !isOverlay) {
			return undefined;
		}
		let start: { x: number; y: number } | null = null;
		const onStart = (event: TouchEvent) => {
			// A pinch is not a swipe.
			start = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
		};
		const onEnd = (event: TouchEvent) => {
			const from = start;
			const touch = event.changedTouches[0];
			start = null;
			if (!from || !touch) {
				return;
			}
			const movedX = touch.clientX - from.x;
			const movedY = touch.clientY - from.y;

			// Leftwards, far enough to be deliberate, and clearly more sideways than not - otherwise
			// every slightly angled scroll through the feed would throw the viewer out of it.
			if (movedX > -STORY_CLOSE_SWIPE_PX || Math.abs(movedX) < Math.abs(movedY) * STORY_CLOSE_SWIPE_RATIO) {
				return;
			}
			handleCloseStoryFeed();
		};
		// Passive: the gesture is read, never prevented, so Swiper keeps its own vertical handling.
		storyFrameEl.addEventListener('touchstart', onStart, { passive: true });
		storyFrameEl.addEventListener('touchend', onEnd, { passive: true });
		storyFrameEl.addEventListener('touchcancel', onEnd, { passive: true });
		return () => {
			storyFrameEl.removeEventListener('touchstart', onStart);
			storyFrameEl.removeEventListener('touchend', onEnd);
			storyFrameEl.removeEventListener('touchcancel', onEnd);
		};
	}, [storyFrameEl, isOverlay, handleCloseStoryFeed]);
	// The logo reuses the preset's existing logotype rather than a new setting, and is withheld over
	// an advertiser's creative.
	// `controls` is what the Story preset populates and what StoryTopControl reads; `topControls`
	// belongs to the other presets. Reading only the latter meant the feed's logo never appeared.
	const storyLogotypeUrl =
		(settings?.controls?.showLogotype ? settings?.controls?.logotypeUrl : undefined) ??
		(settings?.topControls?.showLogotype ? settings?.topControls?.logotypeUrl : undefined);

	if (!mediasData?.length) {
		return null;
	}

	// Styling, so playerStyling - the section the editor writes them to. Numbers arrive as whatever
	// was stored, so a blank field must read as "unset" and fall through to the stylesheet's own
	// defaults rather than as 0 or NaN.
	const storyTextSizeRaw = Number(settings?.playerStyling?.storyTextSize);
	const storyTextSize = Number.isFinite(storyTextSizeRaw) && storyTextSizeRaw > 0 ? storyTextSizeRaw : null;
	const storyTextColor = settings?.playerStyling?.storyTextColor || null;

	return (
		<div
			ref={setStoryFrameEl}
			// An overlay feed covers the page, so it is a modal dialog and has to say so: the name is
			// announced on entry, aria-modal keeps a screen reader's virtual cursor out of the page
			// behind, and tabIndex -1 gives the focus somewhere to land that is not a control.
			{...(isOverlay
				? { role: 'dialog', 'aria-modal': true, 'aria-label': t('LABEL_VIDEO_FEED', 'Video feed'), tabIndex: -1 }
				: {})}
			className={joinClassNames(
				'qplayer-story-softbox',
				isOverlay ? 'qplayer-story-softbox--overlay' : '',
				isStoryAdSlotActive ? 'qplayer-story-softbox--ads' : '',
				// The track is a card plus a teaser tall, so at the end Swiper has nowhere left to
				// scroll and parks the last card a teaser's height down. The control bar is drawn
				// from the card's usual place, so it has to come down with it. Guarded on there
				// being more than one slide: a single-clip feed is "at the end" from the start, and
				// its card has not moved anywhere.
				storyAtEnd && storyVisibleItems.length > 1 ? 'qplayer-story-softbox--at-end' : ''
			)}
			style={
				{
					'--qplayer-story-active-thumb': storyBackdropThumb ? `url(${storyBackdropThumb})` : 'none',
					// The preset's accent, so the feed's own chrome matches the player's.
					'--qplayer-story-accent': settings?.playerStyling?.primaryColor ?? STORY_ACCENT_COLOR,
					// The clip's description, and only that - the stylesheet applies these to the text
					// block alone, so a tooltip or a control label keeps the player's own sizing.
					// Declared here rather than on the text itself: a custom property set on the
					// element that uses it cannot be overridden by one inherited from an ancestor,
					// which is what a preset value is. Absent, the var() fallbacks carry the default.
					...(storyTextSize ? { '--qplayer-story-text-size': `${storyTextSize}px` } : {}),
					...(storyTextColor ? { '--qplayer-story-text-color': storyTextColor } : {}),
				} as React.CSSProperties
			}
		>
			<Swiper
				className="qplayer-story-feed"
				direction="vertical"
				slidesPerView={'auto'}
				// No grabCursor: it painted the whole card `grab`, so crossing any control flipped
				// between two near-identical hand cursors and the pointer appeared to flicker. The
				// card is click-to-pause, so it wears `pointer` throughout and nothing changes.
				followFinger
				simulateTouch
				// On a phone the clip fills the screen, so a gap is a strip of background sliding past
				// mid-swipe and the stack stops reading as one surface. On a desktop the clip is a
				// card with a gutter already around it, and there the gap is what separates one card
				// from the next instead of letting them look like one tall scroll.
				spaceBetween={isUsedOnMobile ? 0 : 16}
				// The feed ENDS at the last clip. Neither loop nor rewind.
				//
				// Loop makes the wrap seamless by keeping copies of the slides at both ends and
				// reshuffling them as the viewer scrolls, and that reshuffle cannot survive the list
				// changing underneath it: retiring an ad slot at the moment the feed wrapped left
				// Swiper with a translate it could not recover from - the clip kept playing and
				// nothing could scroll, by keyboard, button or wheel. Rewind avoids that but makes
				// the wrap a journey back through everything. A feed that simply stops needs neither
				// mechanism, so the slides a viewer can reach are exactly the slides in the list.
				// The step buttons say so at each end; see storyAtStart / storyAtEnd.
				onRealIndexChange={handleStoryIndexChange}
				// Swiper's own pointer engine, not cssMode: cssMode delegates to native scroll-snap,
				// where touch and wheel work but a mouse drag never does.
				touchStartPreventDefault={false}
				threshold={5}
				resistanceRatio={0.65}
				speed={STORY_SLIDE_SPEED_MS}
				navigation={{ prevEl: storyPrevEl, nextEl: storyNextEl }}
				mousewheel={{ forceToAxis: true, thresholdDelta: 6 }}
				keyboard={{ enabled: true, onlyInViewport: false }}
				a11y={{
					containerRoleDescriptionMessage: 'Video feed',
					itemRoleDescriptionMessage: 'Video',
					slideLabelMessage: '{{index}} of {{slidesLength}}',
					prevSlideMessage: t('LABEL_PLAY_PREVIOUS_MEDIA', 'Previous video'),
					nextSlideMessage: t('LABEL_PLAY_NEXT_MEDIA', 'Next video'),
				}}
				modules={[Navigation, Mousewheel, Keyboard, A11y]}
				onSwiper={setStorySwiper}
				onInit={() => {
					const initialMediaId = mediasData[storyMediaInitPosition]?.id;
					setCurrentMediaId(initialMediaId);
					setStoryActiveIndex(storyInitialSlide);
					storyLastIndex.current = storyInitialSlide;
					setIsStoryAdSlotActive(false);
					setIsStoryAdLocked(false);
					// The host page needs the opening clip too, not only the ones scrolled to.
					emitStoryClipChanged(storyVisibleItems[storyInitialSlide], storyInitialSlide, mediasData.length);
					// The opening clip autostarts because its player was created visible, so there is
					// nothing to poke here. This used to wait a flat second and then call play().
					setHasStorySwiperInitialised(true);
				}}
				initialSlide={storyInitialSlide}
			>
				{storyVisibleItems.map((item, index) => {
					// Slides further away than the window stay a poster until they are scrolled near.
					const isMounted = Math.abs(index - storyActiveIndex) <= STORY_SLIDE_WINDOW;

					return (
						<SwiperSlide key={`${item.key}-${configParams?.containerKey}`}>
							{({ isActive }) => {
								// Before Swiper has initialised it reports every slide inactive; the
								// opening one is the active one until it speaks for itself.
								const isSlideActive = hasStorySwiperInitialised
									? isActive
									: index === storyInitialSlide;
								// Dispatched positively, so a third type added later renders
								// nothing rather than silently rendering as an advertisement.
								if (isStoryAdItem(item)) {
									// A slot only requests an ad once. Coming back to it shows a
									// spent card, which cannot ask the ad server for a second break.
									const isConsumed = consumedAdSlots.includes(item.key);
									return (
										<div className="qplayer-story-softbox__slide">
											<div className="qplayer-story-softbox__stage">
												{isMounted && isConsumed && (
													<StoryAdPlaceholder
														isActive={isSlideActive}
														onExpired={() => handleStoryAdFinished(item.key)}
														label={t('LABEL_ADVERTISEMENT', 'Advertisement')}
													/>
												)}
												{isMounted && !isConsumed && (
													<StoryAdSlide
														adTagUrl={storyAdTagUrl}
														isActive={isSlideActive}
														onStarted={() =>
															emitFeedEvent(MessageHelper.MESSAGE_TYPES.adStarted, {
																adSlotIndex: item.adSlotIndex,
																feedIndex: index,
															})
														}
														onSkipped={() =>
															emitFeedEvent(MessageHelper.MESSAGE_TYPES.adSkipped, {
																adSlotIndex: item.adSlotIndex,
																feedIndex: index,
															})
														}
														onFinished={() => {
															// adCompleted covers any break that ran; a skip
															// reports both, in that order.
															emitFeedEvent(MessageHelper.MESSAGE_TYPES.adCompleted, {
																adSlotIndex: item.adSlotIndex,
																feedIndex: index,
															});
															handleStoryAdFinished(item.key);
														}}
														onNoFill={(isOnScreen: boolean) => {
															emitFeedEvent(MessageHelper.MESSAGE_TYPES.adNoFill, {
																adSlotIndex: item.adSlotIndex,
																feedIndex: index,
															});
															setIsStoryAdLocked(false);
															// Dropping the slide the viewer is on makes the feed blink to
															// the next clip - the re-anchor that catches the list change runs
															// at zero duration. It leaves the way a watched break does
															// instead: scroll first, remove once that has played.
															if (isOnScreen) {
																handleStoryAdFinished(item.key);
																return;
															}
															setEmptyAdSlots((previous) =>
																previous.includes(item.key) ? previous : [...previous, item.key]
															);
														}}
														startVolume={
															storyAudibleVolume.current ||
															getStoryAdStartVolume({ settings, configParams })
														}
														muted={isStoryMuted}
														onMutedChange={handleStoryMutedChange}
														skipAfterSeconds={storyAdSkipAfter}
														label={t('LABEL_ADVERTISEMENT', 'Advertisement')}
														noAdLabel={t('LABEL_NO_ADVERTISEMENT', 'No advertisement')}
														muteLabel={t('MUTE_BUTTON')}
														unmuteLabel={t('UNMUTE_BUTTON')}
														playLabel={t('PLAY')}
														pauseLabel={t('PAUSE')}
														skipLabel={t('LABEL_SKIP_AD', 'Skip ad')}
														skipCountdownLabel={t('LABEL_SKIP_AD_IN', 'Skip in {{seconds}}s')}
														// IMA draws its own skip button and countdown; without this they
														// follow the browser rather than the feed. 'dk' is our folder
														// name, 'da' is the language tag - the same swap player-helper
														// makes for GoBrain.
														locale={i18n.language === 'dk' ? 'da' : i18n.language}
													/>
												)}
											</div>
										</div>
									);
								}
								if (isStoryVideoItem(item)) {
									return (
										<StoryVideoSlide
											item={item}
											isActive={isSlideActive}
											isMounted={isMounted}
											hasPicture={storyPictureMediaId === item.media?.id}
											settings={storyNoAdConfig}
											configParams={configParams}
											container={container}
											layout={isUsedOnMobile ? 'mobile' : 'desktop'}
											token={getRestrictToken(currentMediaId ?? playlistId)}
											maintainProportions={
												isUsedOnMobile ? settings?.playbackOptions?.maintainProportions : false
											}
											onPlayComplete={handleStoryVideoEnded}
											onPicture={(mediaId) =>
												setStoryPictureMediaId((current) =>
													current === mediaId ? current : mediaId
												)
											}
											onTogglePlayback={toggleStoryPlayback}
											handlePlayerHover={handlePlayerHover}
										/>
									);
								}
								return null;
							}}
						</SwiperSlide>
					);
				})}
			</Swiper>

			{/* Chrome. Outside the Swiper so the logo, the close and the step controls sit around the
			    video rather than on top of it. */}
			{/* The masthead doubles as a way out, the way a site's own logo does. Kept as the <img>
			    rather than wrapped in a button so the width measurement above still reads the caps
			    and the offset off this element; role and tabIndex make it operable without one. */}
			{/* Kept through an ad slot too. The rest of the chrome gets out of the ad's way, but this
			    is the only way out of the feed on a desktop besides the close button, and a break is
			    the worst moment to take one of the two away. */}
			{storyLogotypeUrl && (
				<img
					ref={setStoryLogoEl}
					className={joinClassNames(
						'qplayer-story-softbox__logo',
						// Dropped rather than allowed onto the card: it belongs in the page's margin.
						storyLogoFits ? '' : 'qplayer-story-softbox__logo--hidden'
					)}
					src={storyLogotypeUrl}
					alt={t('LABEL_CLOSE')}
					role="button"
					tabIndex={0}
					onClick={handleCloseStoryFeed}
					onKeyDown={(event) => {
						if (event.key === 'Enter' || event.key === ' ') {
							event.preventDefault();
							handleCloseStoryFeed();
						}
					}}
				/>
			)}

			{/* Stepping past an ad slot is the ad slide's decision, not the viewer's, so both buttons
			    go dead for its duration and come back with the next video. */}
			{/* Always rendered, never conditionally mounted. Swiper binds these two elements once at
			    init and does not re-bind when they are replaced, so unmounting them - which is what
			    a resize past the gutter threshold used to do - left navigation pointing at detached
			    nodes and the buttons dead for the rest of the session. Hidden with CSS instead:
			    display:none keeps them out of the tab order just as unmounting did, and the elements
			    Swiper holds stay the ones in the document. It also means navigation binds to real
			    elements at init, where storyChromeFits is still false because nothing is measured yet. */}
			<div
				className={joinClassNames(
					'qplayer-story-softbox__nav',
					storyChromeFits ? '' : 'qplayer-story-softbox__nav--hidden'
				)}
			>
					<button
						type="button"
						ref={setStoryPrevEl as any}
						className="qplayer-story-softbox__nav-btn"
						aria-label={t('LABEL_PLAY_PREVIOUS_MEDIA', 'Previous video')}
						// The feed no longer wraps, so at the first clip there is nowhere back to.
						// Said on the control rather than left to a dead press: a button that does
						// nothing when clicked is indistinguishable from one that is broken.
						disabled={isStoryAdLocked || storyAtStart}
					>
						<StoryChevron />
					</button>
					<button
						type="button"
						ref={setStoryNextEl as any}
						className="qplayer-story-softbox__nav-btn"
						aria-label={t('LABEL_PLAY_NEXT_MEDIA', 'Next video')}
						disabled={isStoryAdLocked || storyAtEnd}
					>
						<StoryChevron down />
					</button>
			</div>

			{/* Beside the card or not at all - it never goes onto the video. Same gate as the step
			    buttons, so where they fit it fits. Hidden rather than unmounted, which keeps it out
			    of the tab order just the same and leaves the focus-return target in the document. */}
			{isOverlay && (
				<button
					type="button"
					className={joinClassNames(
						'qplayer-story-softbox__close',
						// No gutter to sit in does not mean no way out. The swipe is there, but it is
						// invisible, and the hint that used to teach it has gone - so on a phone the
						// button moves onto the card's top-left corner instead of being dropped.
						storyChromeFits ? '' : 'qplayer-story-softbox__close--inside'
					)}
					onClick={handleCloseStoryFeed}
					aria-label={t('LABEL_CLOSE')}
				>
					<StoryCross />
				</button>
			)}
		</div>
	);
};

export default StoryFeed;
